"use client";

import { Children, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/format";
import { ChevronIcon } from "./icons";

type Props = {
  label: string;
  // The section's heading and intro, on the left above the row.
  header: ReactNode;
  // A link on the right, such as "Shop all".
  action?: ReactNode;
  // Width classes for each item.
  itemClassName: string;
  children: ReactNode;
};

// A row that scrolls sideways, lined up with the page's margins and bleeding
// off the right edge so it's clear there's more. Phones swipe; wider screens
// also get previous and next buttons, which step by roughly a screenful.
export function Rail({ label, header, action, itemClassName, children }: Props) {
  const track = useRef<HTMLUListElement>(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  const measure = useCallback(() => {
    const el = track.current;
    if (!el) return;
    setEdges({ start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth > el.scrollWidth - 8 });
  }, []);

  useEffect(() => {
    const el = track.current;
    if (!el) return;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  const step = (direction: 1 | -1) => {
    const el = track.current;
    if (el) el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: "smooth" });
  };

  const buttonClass =
    "flex size-10 items-center justify-center rounded-full border border-paper/40 transition-colors hover:border-paper hover:bg-wash disabled:opacity-30 disabled:hover:border-paper/40 disabled:hover:bg-transparent";

  return (
    <div role="region" aria-label={label}>
      <div className="shell flex flex-wrap items-end justify-between gap-x-8 gap-y-3">
        <div className="reveal">{header}</div>
        <div className="flex items-center gap-6">
          {action}
          {edges.start && edges.end ? null : (
            <div className="hidden items-center gap-2 md:flex">
              <button type="button" className={buttonClass} onClick={() => step(-1)} disabled={edges.start} aria-label="Scroll back">
                <ChevronIcon className="size-4 rotate-90" />
              </button>
              <button type="button" className={buttonClass} onClick={() => step(1)} disabled={edges.end} aria-label="Scroll on">
                <ChevronIcon className="size-4 -rotate-90" />
              </button>
            </div>
          )}
        </div>
      </div>
      <ul
        ref={track}
        className="reveal no-scrollbar mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-px-5 px-5 pb-2 md:scroll-px-10 md:px-10 lg:gap-8 xl:px-[max(2.5rem,calc((100vw-82rem)/2+2.5rem))] xl:scroll-px-[max(2.5rem,calc((100vw-82rem)/2+2.5rem))]"
      >
        {Children.map(children, (child) => (
          <li className={cn("shrink-0 snap-start", itemClassName)}>
            {child}
          </li>
        ))}
      </ul>
    </div>
  );
}
